import axios from "axios";
import Button from "components/common/Button";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const EditQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    english: "",
    arabic: "",
    options: [],
    releaseDate: null,
    image: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch question data
    axios
      .get(`/questions/${id}/details`)
      .then((res) => {
        const question = res.data.question;
        setFormState({
          english: question.english,
          arabic: question.arabic || "",
          options: question.options,
          releaseDate: question.releaseDate ? new Date(question.releaseDate) : null,
          image: question.image || "",
        });
        if (question.image) {
          setImagePreview(question.image);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to load question");
        setLoading(false);
      });
  }, [id]);

  const addOption = () => {
    setFormState((prev) => ({
      ...prev,
      options: [...prev.options, { english: "", arabic: "", isCorrect: false }],
    }));
  };

  const deleteOption = (idx) => {
    setFormState((prev) => {
      const newOptions = [...prev.options];
      newOptions.splice(idx, 1);
      return {
        ...prev,
        options: newOptions,
      };
    });
  };

  const updateIsCorrect = (e, idx) => {
    const { checked } = e.target;

    setFormState((prev) => {
      const newOptions = [...prev.options];
      newOptions[idx].isCorrect = checked;
      return { ...prev, options: newOptions };
    });
  };

  const updateOptionVal = (e, language, idx) => {
    const { value } = e.target;

    setFormState((prev) => {
      const newOptions = [...prev.options];
      newOptions[idx][language] = value;
      return { ...prev, options: newOptions };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormState((prev) => ({ ...prev, image: "" }));
  };

  const formSubmitHandler = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imagePath = formState.image;

      // Upload image if a new one is selected
      if (imageFile) {
        const formData = new FormData();
        formData.append("image", imageFile);

        const uploadRes = await axios.post("/questions/upload-image", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        imagePath = uploadRes.data.filePath;
      }

      // Update question with image path
      const formDataToSubmit = {
        ...formState,
        releaseDate: formState.releaseDate ? formState.releaseDate.toISOString().split('T')[0] : null,
        image: imagePath
      };
      await axios.put(`/questions/${id}`, formDataToSubmit);
      
      toast.success("Question updated successfully!");
      navigate("/questions-list");
    } catch (err) {
      console.log(err);
      toast.error(
        (err?.response?.data?.errors && err?.response?.data?.errors[0]) ||
          "Something went wrong while updating the question"
      );
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 sm:py-24 px-4 sm:px-6 md:px-10 flex justify-center items-center min-h-[60vh]">
        <div className="text-xl sm:text-2xl text-slate-500 font-medium">Loading...</div>
      </div>
    );
  }

  return (
    <div className="py-20 sm:py-24 px-4 sm:px-6 md:px-10">
      <div className="max-w-3xl mx-auto">
        <Link to="/questions-list">
          <h5 className="text-xl sm:text-2xl md:text-3xl text-emerald-600 underline decoration-emerald-200 hover:decoration-emerald-500 text-center mb-4 sm:mb-5 font-semibold transition-colors">
            Back to Questions List
          </h5>
        </Link>

        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 text-slate-800">Edit Question</h2>

        <form onSubmit={formSubmitHandler} className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-5 sm:p-6 md:p-8 shadow-xl space-y-5">
          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-eng">
              Question statement (English)
            </label>
            <textarea
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none bg-white/60 text-base transition-all"
              type="text"
              value={formState.english}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, english: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-ara">
              Question statement (Arabic) - Optional
            </label>
            <textarea
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none text-right bg-white/60 text-base transition-all"
              type="text"
              value={formState.arabic}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, arabic: e.target.value }))
              }
              placeholder="اختياري"
            />
          </div>

          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-img">
              Question Image - Optional
            </label>
            <input
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none bg-white/60 text-sm sm:text-base transition-all file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
              type="file"
              id="question-img"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div className="relative mt-3">
                <img
                  src={imagePreview.startsWith('/uploads') ? `${axios.defaults.baseURL.replace('/api', '')}${imagePreview}` : imagePreview}
                  alt="Preview"
                  className="max-w-full max-h-48 sm:max-h-64 rounded-xl"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                >
                  &times;
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-date">
              Release Date
            </label>
            <DatePicker
              selected={formState.releaseDate}
              onChange={(date) => setFormState((prev) => ({ ...prev, releaseDate: date }))}
              minDate={new Date()}
              placeholderText="Select release date"
              dateFormat="yyyy-MM-dd"
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none bg-white/60 text-base transition-all"
              wrapperClassName="block w-full"
            />
          </div>

          <div className="border-t border-b border-slate-200 py-5">
            <h4 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">Options</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {formState.options.map((el, idx) => {
                return (
                  <div key={"question-option-" + idx} className="border-2 border-slate-200 rounded-xl p-4 bg-white/60 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-center mb-3">
                      <div className="font-semibold text-emerald-600 text-sm sm:text-base">Option {idx + 1}</div>
                      <img
                        className="cursor-pointer hover:scale-110 transition-transform w-5 h-5"
                        onClick={() => deleteOption(idx)}
                        src="/assets/imgs/bin.svg"
                        alt="bin"
                      />
                    </div>

                    <input
                      className="mb-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-2.5 outline-none bg-white/60 text-sm sm:text-base transition-all"
                      placeholder="English"
                      type="text"
                      value={el.english}
                      onChange={(e) => updateOptionVal(e, "english", idx)}
                    />
                    <input
                      className="mb-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-2.5 outline-none text-right bg-white/60 text-sm sm:text-base transition-all"
                      placeholder="Arabic (Optional - اختياري)"
                      type="text"
                      value={el.arabic}
                      onChange={(e) => updateOptionVal(e, "arabic", idx)}
                    />

                    <div className="inline-flex items-center mt-1">
                      <label htmlFor={el.english + idx} className="mr-3 text-sm text-slate-600">
                        Is Correct?
                      </label>
                      <span
                        id={el.english + idx}
                        className="relative flex items-center p-3 rounded-full cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-slate-300 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-emerald-500 checked:bg-emerald-500 checked:before:bg-emerald-500 hover:before:opacity-10"
                          checked={el.isCorrect}
                          onChange={(e) => updateIsCorrect(e, idx)}
                        />
                        <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="1"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        </span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="pt-4 flex justify-center">
              <Button type="button" onClick={addOption}>
                Add Option
              </Button>
            </div>
          </div>

          <div className="pt-3 flex justify-center gap-4">
            <Button type="submit" disabled={uploading}>
              {uploading ? "Updating..." : "Update Question"}
            </Button>
            <Link to="/questions-list">
              <button
                type="button"
                className="h-12 sm:h-14 md:h-16 px-6 sm:px-8 md:px-10 bg-slate-500 hover:bg-slate-600 text-white rounded-xl font-semibold text-base sm:text-lg md:text-xl transition-all hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 shadow-md"
              >
                Cancel
              </button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditQuestion;
